<template>
  <div class="tracker-page">
    <div class="tracker-header-title">
      <h1>Tracker</h1>
      <p>Track your recurring tasks and see when they are due next.</p>
    </div>

    <div class="tracker-content">
      <div class="tracker-left">
        <div class="add-action">
          <button class="btn btn-primary" @click="openModal()">+ Add Item</button>
        </div>
        <TrackerList @edit="openModal" />
      </div>

      <div class="tracker-right">
        <div class="calendar-section">
          <h3>Calendar View</h3>
          <div class="calendar-wrapper">
            <VCalendar 
              :attributes="calendarAttributes" 
              is-expanded
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Modal for Add/Edit -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <h2>{{ editingItem ? 'Edit Tracker' : 'Add Tracker' }}</h2>
        <form @submit.prevent="saveItem">
          <div class="form-group">
            <label>Item Name</label>
            <input type="text" v-model="form.itemName" required />
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="form.hasCompletedBefore" />
              Has been completed before?
            </label>
            <div v-if="form.hasCompletedBefore" style="margin-top: 10px;">
              <label>Last Completed</label>
              <input type="date" v-model="form.lastCompleted" required />
            </div>
          </div>

          <div class="form-group">
            <label>Type</label>
            <div class="radio-group">
              <label class="radio-label">
                <input type="radio" :value="false" v-model="form.isRecurring" /> One-time
              </label>
              <label class="radio-label">
                <input type="radio" :value="true" v-model="form.isRecurring" /> Recurring
              </label>
            </div>
          </div>

          <div class="form-group">
            <label>{{ form.isRecurring ? 'Start Date' : 'Due Date' }}</label>
            <input type="date" v-model="form.nextDue" required />
          </div>

          <div class="form-group recurrence-group" v-if="form.isRecurring">
            <label>Recurrence</label>
            <div class="recurrence-inputs">
              <input type="number" v-model="form.recurrenceValue" min="1" required />
              <select v-model="form.recurrenceUnit" required>
                <option value="days">Days</option>
                <option value="weeks">Weeks</option>
                <option value="months">Months</option>
                <option value="years">Years</option>
              </select>
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
            <button type="submit" class="btn btn-primary">Save</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import TrackerList from '@/components/TrackerList.vue';
import 'v-calendar/dist/style.css';
import { Calendar as VCalendar } from 'v-calendar';

export default {
  name: 'TrackerPage',
  components: {
    TrackerList,
    VCalendar
  },
  setup() {
    const store = useStore();
    const showModal = ref(false);
    const editingItem = ref(null);

    const getLocalYMD = (dateOrString) => {
      if (!dateOrString) return '';
      const d = typeof dateOrString === 'string' ? new Date(dateOrString) : dateOrString;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    const getTodayStr = () => getLocalYMD(new Date());
    const getNextWeekStr = () => {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      return getLocalYMD(d);
    };

    const form = ref({
      itemName: '',
      hasCompletedBefore: false,
      isRecurring: true,
      lastCompleted: getTodayStr(),
      nextDue: getNextWeekStr(),
      recurrenceValue: 1,
      recurrenceUnit: 'weeks'
    });

    const items = computed(() => store.getters['tracker/getItems'] || []);

    const calendarAttributes = computed(() => {
      const attrs = [];
      
      items.value.forEach(item => {
        // 1. Add Next Due dot (only if not a completed one-time item)
        if (!item.isCompleted) {
          attrs.push({
            key: `due-${item.id}`,
            dot: 'blue',
            popover: {
              label: `Due: ${item.itemName}`,
            },
            dates: new Date(item.nextDue),
          });
        }
        
        // 2. Add History dots (from history array, or fallback to lastCompleted for older items)
        const historyDates = item.history ? [...item.history] : [];
        if (historyDates.length === 0 && item.lastCompleted) {
          historyDates.push(item.lastCompleted);
        }
        
        historyDates.forEach((histDate, index) => {
          attrs.push({
            key: `hist-${item.id}-${index}`,
            dot: 'green',
            popover: {
              label: `${item.itemName} - completed`,
            },
            dates: new Date(histDate),
          });
        });
      });
      
      return attrs;
    });

    const openModal = (item = null) => {
      if (item) {
        editingItem.value = item;
        form.value = {
          ...item,
          hasCompletedBefore: !!item.lastCompleted,
          isRecurring: item.isRecurring !== false, // default to true if undefined for backwards compatibility
          lastCompleted: item.lastCompleted ? getLocalYMD(item.lastCompleted) : getTodayStr(),
          nextDue: item.nextDue ? getLocalYMD(item.nextDue) : getNextWeekStr(),
        };
      } else {
        editingItem.value = null;
        form.value = {
          itemName: '',
          hasCompletedBefore: false,
          isRecurring: true,
          lastCompleted: getTodayStr(),
          nextDue: getNextWeekStr(),
          recurrenceValue: 1,
          recurrenceUnit: 'weeks'
        };
      }
      showModal.value = true;
    };

    const closeModal = () => {
      showModal.value = false;
      editingItem.value = null;
    };

    const saveItem = async () => {
      // Add T12:00:00 to prevent YYYY-MM-DD from parsing as UTC and shifting back a day
      const nextDueDate = new Date(form.value.nextDue + "T12:00:00");
      const lastCompletedDate = form.value.hasCompletedBefore ? new Date(form.value.lastCompleted + "T12:00:00") : null;
      
      const itemData = {
        itemName: form.value.itemName,
        isRecurring: form.value.isRecurring,
        lastCompleted: lastCompletedDate ? lastCompletedDate.toISOString() : null,
        nextDue: nextDueDate.toISOString(),
        recurrenceValue: form.value.isRecurring ? form.value.recurrenceValue : null,
        recurrenceUnit: form.value.isRecurring ? form.value.recurrenceUnit : null
      };

      if (editingItem.value) {
        await store.dispatch('tracker/updateItem', { id: editingItem.value.id, ...itemData });
      } else {
        await store.dispatch('tracker/addItem', itemData);
      }
      closeModal();
    };

    return {
      showModal,
      editingItem,
      form,
      calendarAttributes,
      openModal,
      closeModal,
      saveItem
    };
  }
};
</script>

<style scoped lang="scss">
.tracker-page {
  margin: 0 auto;
  width: 100%;
}

.tracker-header-title {
  text-align: center;
  margin-bottom: 20px;
}

.tracker-content {
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
}

@media (min-width: 1000px) {
  .tracker-content {
    grid-template-columns: 350px 1fr;
  }
}

.add-action {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  font-size: 16px;
}

.btn-primary {
  background-color: rgba(6, 182, 212, 0.1);
  color: #06b6d4;
  border: 1px solid rgba(6, 182, 212, 0.3);
}
.btn-primary:hover { background-color: rgba(6, 182, 212, 0.2); }

.btn-secondary {
  background-color: rgba(148, 163, 184, 0.1);
  color: #94a3b8;
  border: 1px solid rgba(148, 163, 184, 0.3);
}
.btn-secondary:hover { background-color: rgba(148, 163, 184, 0.2); }

.divider {
  margin: 40px 0;
  border: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.calendar-section {
  text-align: center;
}

.calendar-wrapper {
  display: block;
  width: 100%;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(12px);
  padding: 20px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
}

.calendar-wrapper :deep(.vc-container) {
  width: 100%;
  min-height: 400px;
}

.calendar-wrapper :deep(.vc-pane) {
  width: 100%;
}

.calendar-wrapper :deep(.vc-title) {
  color: white;
  font-size: 1.1rem; /* Make the month/year header larger */
  font-weight: bold;
  padding: 0px 10px 0px 10px;
}

.calendar-wrapper :deep(.vc-weeks) {
  /* Fix double borders by using gap or overlapping, simple borders on cells is usually enough */
}

.calendar-wrapper :deep(.vc-arrow) {
  color: white;
}

.calendar-wrapper :deep(.vc-day) {
  min-height: 80px; 
  border: 1px solid #e2e8f0;
}

.calendar-wrapper :deep(.vc-day-content) {

  font-size: 1.2rem;
  font-weight: 500;
  width: 100%;
  height: 100%;
}

.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 30px;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  color: $white;
}

.form-group {
  margin-bottom: 15px;
  text-align: left;
}

.form-group label {
  display: block;
  font-weight: bold;
  margin-bottom: 5px;
}

.form-group input:not([type="checkbox"]), .form-group select {
  width: 100%;
  padding: 10px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: $white;
  box-sizing: border-box;
}

.checkbox-label {
  display: flex !important;
  align-items: center;
  gap: 8px;
  font-weight: normal !important;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: auto;
  margin: 0;
  cursor: pointer;
}

.recurrence-inputs {
  display: flex;
  gap: 10px;
}

.radio-group {
  display: flex;
  gap: 15px;
  margin-top: 5px;
}

.radio-label {
  display: flex !important;
  align-items: center;
  gap: 8px;
  font-weight: normal !important;
  cursor: pointer;
}

.radio-label input[type="radio"] {
  width: auto;
  margin: 0;
  cursor: pointer;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}
</style>
